import { toast as sonnerToast } from 'sonner';

import { adminCopy, clinicalCopy, formatResourceCopy } from './copy';

const SUCCESS_DURATION = 4000;
const SUCCESS_WITH_SIDE_EFFECT_DURATION = 6000;
const ERROR_DURATION = 8000;

export const toast = {
  // Existing helpers — unchanged behaviour.
  createdResource(resource: string): void {
    sonnerToast.success(formatResourceCopy(adminCopy.success.create, resource));
  },

  updated(): void {
    sonnerToast.success(adminCopy.success.update);
  },

  deletedResource(resource: string): void {
    sonnerToast.success(formatResourceCopy(adminCopy.success.delete, resource));
  },

  invited(email: string): void {
    sonnerToast.success(formatResourceCopy(adminCopy.success.invite, 'user', { email }));
  },

  error(message: string): void {
    sonnerToast.error(message, { duration: ERROR_DURATION });
  },

  network(): void {
    sonnerToast.error(adminCopy.error.network, { duration: ERROR_DURATION });
  },

  shapeMismatch(): void {
    sonnerToast.error(adminCopy.error.shapeMismatch, { duration: ERROR_DURATION });
  },

  // Clinical helpers — Part A §5.4. Do not create a parallel toast module.
  reportSubmitted(): void {
    sonnerToast.success(clinicalCopy.toast.reportSubmitted, { duration: SUCCESS_DURATION });
  },

  reportApproved(): void {
    sonnerToast.success(clinicalCopy.toast.reportApproved, { duration: SUCCESS_DURATION });
  },

  reportRejected(): void {
    sonnerToast.success(clinicalCopy.toast.reportRejected, { duration: SUCCESS_DURATION });
  },

  noteSubmitted(): void {
    sonnerToast.success(clinicalCopy.toast.noteSubmitted, { duration: SUCCESS_DURATION });
  },

  flagRaised(): void {
    sonnerToast.success(clinicalCopy.toast.flagRaised, {
      duration: SUCCESS_WITH_SIDE_EFFECT_DURATION,
    });
  },

  phiRateLimited(): void {
    sonnerToast.error(clinicalCopy.toast.phiRateLimited, { duration: ERROR_DURATION });
  },

  flagRaiseRateLimited(): void {
    sonnerToast.error(clinicalCopy.toast.flagRaiseRateLimited, { duration: ERROR_DURATION });
  },

  approvalRace(): void {
    sonnerToast.error(clinicalCopy.toast.approvalRace, { duration: ERROR_DURATION });
  },

  slotTaken(): void {
    sonnerToast.error(clinicalCopy.toast.slotTaken, { duration: ERROR_DURATION });
  },

  // Plan 2b non-blocking call errors: 4s dwell (vs the 8s ERROR_DURATION
  // default) because the call continues for the user — a shorter toast
  // keeps the video surface uncluttered. callEndFailed gets 6s so the
  // "disconnecting locally" hint has dwell time.
  recordingStartFailed(): void {
    sonnerToast.error(clinicalCopy.toast.recordingStartFailed, { duration: 4000 });
  },

  recordingStopFailed(): void {
    sonnerToast.error(clinicalCopy.toast.recordingStopFailed, { duration: 4000 });
  },

  participantActionFailed(): void {
    sonnerToast.error(clinicalCopy.toast.participantActionFailed, { duration: 4000 });
  },

  callEndFailed(): void {
    sonnerToast.error(clinicalCopy.toast.callEndFailed, {
      duration: 6000,
    });
  },

  /** Leave & complete raced an already-completed row and part of the
   *  wrap-up could not be saved. Longer dwell: it tells the practitioner
   *  where to go to fix it. */
  wrapUpNotSaved({ what }: { what: 'concerns' | 'risk' }): void {
    sonnerToast.error(
      what === 'concerns'
        ? clinicalCopy.toast.wrapUpConcernsNotSaved
        : clinicalCopy.toast.wrapUpRiskNotSaved,
      { duration: 8000 },
    );
  },

  mediaDeviceError(kind: 'camera' | 'mic'): void {
    const copy =
      kind === 'camera'
        ? clinicalCopy.toast.cameraDeviceError
        : clinicalCopy.toast.microphoneDeviceError;
    sonnerToast.error(copy, { duration: 4000 });
  },

  saveFailed(): void {
    sonnerToast.error(clinicalCopy.toast.saveFailed, { duration: ERROR_DURATION });
  },

  noteSubmitFailed(): void {
    sonnerToast.error(clinicalCopy.toast.noteSubmitFailed, { duration: ERROR_DURATION });
  },

  reportSubmitFailed(): void {
    sonnerToast.error(clinicalCopy.toast.reportSubmitFailed, { duration: ERROR_DURATION });
  },

  approveFailed(): void {
    sonnerToast.error(clinicalCopy.toast.approveFailed, { duration: ERROR_DURATION });
  },

  rejectFailed(): void {
    sonnerToast.error(clinicalCopy.toast.rejectFailed, { duration: ERROR_DURATION });
  },

  exerciseAssigned(): void {
    sonnerToast.success(clinicalCopy.toast.exerciseAssigned, { duration: SUCCESS_DURATION });
  },

  exerciseReviewed(): void {
    sonnerToast.success(clinicalCopy.toast.exerciseReviewed, {
      duration: SUCCESS_WITH_SIDE_EFFECT_DURATION,
    });
  },

  exerciseAssignFailed(): void {
    sonnerToast.error(clinicalCopy.toast.exerciseAssignFailed, { duration: ERROR_DURATION });
  },

  exerciseReviewFailed(): void {
    sonnerToast.error(clinicalCopy.toast.exerciseReviewFailed, { duration: ERROR_DURATION });
  },

  // Telephony: a call started ringing at one of the user's locations. Info,
  // not success, and the longer dwell so there is time to open the pool.
  callPlaced(): void {
    sonnerToast.success('Call placed', {
      description: 'Answer it in the Ozonetel toolbar. The session is open here.',
      duration: SUCCESS_WITH_SIDE_EFFECT_DURATION,
    });
  },

  incomingCall(callerLabel: string): void {
    sonnerToast.info(`Incoming call: ${callerLabel}`, {
      description: 'Open Calls to claim it.',
      duration: SUCCESS_WITH_SIDE_EFFECT_DURATION,
    });
  },
} as const;
